import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Film,
  X,
} from 'lucide-react';
import { useId } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ContentBlockWarningsItem } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, useMedia } from '@/features/media';

import type { BannerFormInput, MediaRefForm } from '../../schemas/block-form-schema';

import { BannerItemFields } from './banner-item-fields';

export interface SlideCardProps {
  index: number;
  count: number;
  storeId: string | null;
  expanded: boolean;
  /** Read-time warnings of the saved block; the ones under `banner.items.<index>` mark this slide. */
  warnings: readonly ContentBlockWarningsItem[];
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function LibraryThumb({ mediaId, className }: { mediaId: string; className?: string }) {
  const { data } = useMedia(mediaId);
  return <MediaThumb media={data} className={className} />;
}

/** Library media through its row, URL media as a plain image, videos as an icon. */
function SlideThumb({ media }: { media: MediaRefForm | undefined }) {
  const className = 'size-10 shrink-0 rounded-md border';
  if (!media) return <MediaThumb media={null} className={className} />;
  if (media.type === 'library')
    return <LibraryThumb mediaId={media.mediaId} className={className} />;
  if (media.kind === 'video' || media.url === '') {
    return (
      <div
        className={cn(className, 'bg-muted text-muted-foreground flex items-center justify-center')}
        aria-hidden
      >
        <Film className="size-4" />
      </div>
    );
  }
  return <img src={media.url} alt="" className={cn(className, 'object-cover')} loading="lazy" />;
}

/**
 * One slide of a banner as a collapsible card. Collapsed it is a summary row (position,
 * thumbnail, title, button count, a warning dot when the slide has a validation error or a
 * read-time warning); expanded it holds the full item fields.
 */
export function SlideCard({
  index,
  count,
  storeId,
  expanded,
  warnings,
  onToggle,
  onMoveUp,
  onMoveDown,
  onRemove,
}: SlideCardProps) {
  const { t } = useTranslation('content');
  const panelId = useId();
  const form = useFormContext<BannerFormInput>();
  const item = useWatch({ control: form.control, name: `items.${index}` });
  const { errors } = useFormState({ control: form.control, name: `items.${index}` });
  const hasError = errors.items?.[index] !== undefined;
  const hasWarning = warnings.some(
    (warning) =>
      warning.path.startsWith(`banner.items.${index}.`) || warning.path === `banner.items.${index}`,
  );
  const flagged = hasError || hasWarning;
  const title = item.title.trim();
  const ctaCount = item.ctas.length;

  return (
    <li
      className={cn('rounded-md border', expanded && 'border-primary/40')}
      data-expanded={expanded}
      aria-label={t('slide.nth', { index: index + 1 })}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="focus-visible:ring-ring/50 flex min-w-0 flex-1 items-center gap-3 rounded-sm text-left focus-visible:ring-[3px] focus-visible:outline-none"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={
            expanded
              ? t('slide.collapse', { index: index + 1 })
              : t('slide.expand', { index: index + 1 })
          }
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown className="text-muted-foreground size-4 shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
          )}
          <span className="text-muted-foreground w-5 shrink-0 text-right text-sm tabular-nums">
            {index + 1}
          </span>
          <SlideThumb media={item.media} />
          <span className="min-w-0 flex-1">
            <span
              className={cn(
                'block truncate text-sm font-medium',
                !title && 'text-muted-foreground italic',
              )}
            >
              {title || t('slide.untitled')}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {t('slide.ctaCount', { count: ctaCount })}
            </span>
          </span>
          {flagged ? (
            <AlertTriangle
              className="text-destructive size-4 shrink-0"
              role="img"
              aria-label={t('slide.hasIssues')}
            />
          ) : null}
        </button>
        <span className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={index === 0}
            onClick={onMoveUp}
            aria-label={t('slide.moveUp', { index: index + 1 })}
          >
            <ArrowUp aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={index === count - 1}
            onClick={onMoveDown}
            aria-label={t('slide.moveDown', { index: index + 1 })}
          >
            <ArrowDown aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label={t('slide.remove', { index: index + 1 })}
          >
            <X aria-hidden />
          </Button>
        </span>
      </div>
      {expanded ? (
        <div id={panelId} className="border-t p-4">
          <BannerItemFields name={`items.${index}`} storeId={storeId} />
        </div>
      ) : null}
    </li>
  );
}
